// Defensive mapper from an unknown server payload into a ScheduleView.
// Returns null on malformed input so callers can drop bad rows without
// crashing the render. Extra fields are ignored; missing required
// fields produce null.

import type { ScheduleStatus, ScheduleView } from './types';

const STATUSES: ReadonlyArray<ScheduleStatus> = ['inactive', 'active', 'paused'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function toOptionalNullableString(value: unknown): string | null | undefined {
  if (value === null) return null;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

export function toScheduleView(raw: unknown): ScheduleView | null {
  if (!isRecord(raw)) {
    return null;
  }

  const { id, name, cron, status } = raw;

  if (typeof id !== 'string' || id.length === 0) return null;
  if (typeof name !== 'string' || name.length === 0) return null;
  if (typeof status !== 'string' || !STATUSES.includes(status as ScheduleStatus)) {
    return null;
  }
  const mappedCron = toOptionalNullableString(cron);
  if (mappedCron === undefined) return null;

  const view: ScheduleView = {
    id,
    name,
    cron: mappedCron,
    status: status as ScheduleStatus,
  };

  const last = toOptionalString(raw.lastRunAt ?? raw.last_run_at);
  if (last !== undefined) view.lastRunAt = last;
  const next = toOptionalString(raw.nextRunAt ?? raw.next_run_at);
  if (next !== undefined) view.nextRunAt = next;

  return view;
}
